import { addOfferItem, removeOfferItem, removeOfferMultiSelectedItem } from 'actions/proposal';
import { trackUserInteraction } from 'actions/tracking';
import * as t from 'data';
import { formatPrice, multiProducts } from 'helpers';
import { useDispatch, useHistory, useSelector } from 'hooks';
import { pick, find, isEmpty } from 'lodash';
import { Icon, CustomOfferItemPreviewV2 } from 'mymoria-ui/components';
import { useToast } from 'mymoria-ui/hooks';
import React, { FunctionComponent, useMemo } from 'react';

import { useRouteMatch } from 'react-router-dom';
import { isOfferEditable } from 'reducers/proposal';
import { OfferItemType, Product, Service } from 'types';
import { customizeByLineItemId } from 'utils';

interface OptionalItemPreviewProps {
  id: string;
  type: OfferItemType;
  lineItemId?: string;
  children?: (selectedItem?: Product | Service) => React.ReactNode;
}

const OptionalItemPreview: FunctionComponent<OptionalItemPreviewProps> = ({
  id,
  type,
  lineItemId,
  children,
}) => {
  const dispatch = useDispatch();
  const { url } = useRouteMatch();
  const { push } = useHistory();
  const funeralType = useSelector(({ proposal }) => proposal.funeralType);

  const isEditable = useSelector(isOfferEditable());
  const { addToast } = useToast();
  const selectedItemsIds = useSelector(
    ({ proposal }) => proposal[type === 'services' ? 'optionalServices' : 'optionalProducts'],
  );
  const allItems = useSelector(({ entities }) => entities[type]);
  const selectedItems = pick(allItems, selectedItemsIds);
  const mostRecentItem = useSelector(({ entities }) => entities[type][id]);
  const favoriteProducts = useSelector(({ products }) => products.favorite);
  const customizations = useSelector(({ proposal }) => proposal.customizations);
  const selectedProducts = useSelector(({ entities }) => entities.selectedProducts);
  const selectedServices = useSelector(({ entities }) => entities.selectedServices);

  let selectedItemsCustomized: {
    products: Product[];
    services: Service[];
  } = {
    products: [],
    services: [],
  };

  if (lineItemId) {
    if (customizations?.services) {
      selectedItemsCustomized.services = selectedItemsCustomized.services.concat(
        customizeByLineItemId(selectedServices, customizations?.services),
      );
    }
    if (customizations?.products) {
      selectedItemsCustomized.products = selectedItemsCustomized.products.concat(
        customizeByLineItemId(selectedProducts, customizations?.products),
      );
    }
  }
  const selectedLineItems = [
    ...selectedItemsCustomized.products,
    ...selectedItemsCustomized.services,
  ];
  let shouldRender: boolean = true;

  // check if any of selected proposal item match with one of the defaults
  const foundSelectedOptionalItem = lineItemId
    ? find(selectedLineItems, entity => entity.lineItemId === lineItemId)
    : find(selectedItems, entity => entity.id === mostRecentItem.id);

  let item = foundSelectedOptionalItem || mostRecentItem;

  const isProduct = type === 'products';

  const category = !isProduct ? undefined : (item as Product)?.category;
  const isSelected = !!foundSelectedOptionalItem;
  const isMultiProduct = category ? multiProducts.includes(category) : false;

  // Use useMemo to memoize favoriteProductsIds to avoid recalculation on every render
  const favoriteProductsIds = useMemo(
    () => (category && !isEmpty(favoriteProducts) ? favoriteProducts[funeralType][category] : []),
    [category, favoriteProducts, funeralType],
  );

  if (!item) {
    return null;
  }
  const itemIdentifier = item.id;
  const isNotLastItem =
    category &&
    Object.values(selectedItems).filter(item => (item as Product).category === category).length > 1;

  const handleEdit = () => {
    dispatch(trackUserInteraction('Edit Product', item.translations.shortname));
    push(`${url}/products/${category}`);
  };

  const handleSelect = () => {
    try {
      return isSelected
        ? isNotLastItem
          ? dispatch(removeOfferMultiSelectedItem(itemIdentifier, type))
          : dispatch(removeOfferItem(itemIdentifier, type, category))
        : dispatch(addOfferItem(itemIdentifier, type));
    } catch {
      return addToast(t.errors.general);
    }
  };

  shouldRender =
    !isProduct || isEmpty(favoriteProductsIds) || (isSelected && !isEmpty(favoriteProductsIds));

  return (
    <>
      {shouldRender && (
        <CustomOfferItemPreviewV2
          id={itemIdentifier}
          name={item.translations.shortname}
          price={item.price}
          priceFormatter={formatPrice}
          image={item.pictures[0] || item.placeholder}
          selected={isEditable && isSelected}
          isEditable={isEditable}
          onSelect={isEditable ? handleSelect : undefined}
          onClick={isEditable && type === 'products' ? handleEdit : undefined}
          description={item.translations.description}
          actionIcon={isMultiProduct ? <Icon.Plus /> : <Icon.Pencil />}
          actionLabel={
            category && isMultiProduct ? t.offer.editProduct[category] : t.global.labels.edit
          }
          quantity={item.quantity}
        />
      )}
      {children && children(foundSelectedOptionalItem)}
    </>
  );
};

export default OptionalItemPreview;
